import { useMemo } from 'react';
import type { ISelectInputProps, SelectOption } from '../SelectInput';

/**
 * Ritorna l'etichetta accessibile dell'opzione, in formato stringa.
 */
export function getAccessibleLabelFromOption(
  option: SelectOption | undefined,
  filterProp: string,
) {
  if (!option) return null;

  const label =
    option[filterProp as keyof SelectOption] ??
    (typeof option.label === 'string' ? option.label : option.title ?? null);

  if (label != null) return label as string;

  // console.warn(`Missing label for option with value "${option.value}"`);
  return option.value;
}

/**
 * Ritorna la label dell'opzione selezionata.
 */
export function useSelectedOptionLabel(
  value: string | string[],
  options: SelectOption[] | null | undefined,
  mode: ISelectInputProps['mode'],
  filterProp: string,
) {
  return useMemo(() => {
    if (!value || (Array.isArray(value) && value.length === 0)) return null;

    // Single-select mode
    if (mode == null) {
      const option = options?.find(o => o.value === value);
      return getAccessibleLabelFromOption(option, filterProp);
    }

    if (!Array.isArray(value)) {
      console.warn(
        `[useSelectedOptionLabel] Invalid value for mode "${mode}"`,
        value,
      );
      return null;
    }

    // Array-select mode
    return options
      ?.filter(o => value?.includes(o.value as string))
      .map(o => getAccessibleLabelFromOption(o, filterProp))
      .filter(Boolean)
      .join(', ');
  }, [value, options, mode, filterProp]);
}
